import React, { useState, useEffect } from 'react';
import { Home, Edit2, Trash2, TreePine, Waves, Building2, Mountain, Key, Crown, Castle, Droplets, FerrisWheel, Palmtree, Plus } from 'lucide-react';
import { DVC_RESORTS, USE_YEAR_MONTHS, getAvailableResorts } from '../data/dvcData';
import { DVCContract } from '../utils/dvcLogic';

const ContractManager = ({ contracts, setContracts }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [formData, setFormData] = useState({
    homeResort: '',
    useYearMonth: 1,
    annualPoints: '',
    currentYearPoints: '',
    nextYearPoints: ''
  });
  const [availableResorts, setAvailableResorts] = useState({});

  // Load available resorts from JSON on component mount
  useEffect(() => {
    const resorts = getAvailableResorts();
    setAvailableResorts(resorts);
  }, []);

  // Icon mapping for resort themes
  const getResortIcon = (iconName) => {
    const iconMap = {
      'TreePine': TreePine,
      'Ferris-wheel': FerrisWheel,
      'Waves': Waves,
      'Building2': Building2,
      'Mountain': Mountain,
      'Key': Key,
      'Palmtree': Palmtree,
      'Castle': Castle,
      'Droplets': Droplets,
      'Crown': Crown
    };
    return iconMap[iconName] || Home;
  };

  const resetForm = () => {
    setFormData({
      homeResort: '',
      useYearMonth: 1,
      annualPoints: '',
      currentYearPoints: '',
      nextYearPoints: ''
    });
    setEditingContract(null);
    setShowForm(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.homeResort || !formData.annualPoints) {
      alert('Please fill in all required fields');
      return;
    }

    const currentYear = new Date().getFullYear();
    const annualPoints = parseInt(formData.annualPoints);
    const currentYearPoints = formData.currentYearPoints !== '' ? parseInt(formData.currentYearPoints) : annualPoints;
    const nextYearPoints = formData.nextYearPoints !== '' ? parseInt(formData.nextYearPoints) : annualPoints;
    
    const contractData = {
      ...formData,
      annualPoints: annualPoints,
      pointsRemaining: {
        [currentYear]: currentYearPoints,
        [currentYear + 1]: nextYearPoints,
        [currentYear + 2]: annualPoints,
        [currentYear + 3]: annualPoints,
        [currentYear + 4]: annualPoints
      }
    };

    if (editingContract) {
      // Update existing contract
      const updatedContracts = contracts.map(contract => 
        contract.id === editingContract.id 
          ? new DVCContract(
              contract.id,
              contractData.homeResort,
              contractData.useYearMonth,
              contractData.annualPoints,
              new Date().getFullYear(),
              contractData.pointsRemaining
            )
          : contract
      );
      setContracts(updatedContracts);
    } else {
      // Add new contract
      const newContract = new DVCContract(
        Date.now().toString(),
        contractData.homeResort,
        contractData.useYearMonth,
        contractData.annualPoints,
        new Date().getFullYear(),
        contractData.pointsRemaining
      );
      setContracts([...contracts, newContract]);
    }

    resetForm();
  };

  const handleEdit = (contract) => {
    const currentYear = new Date().getFullYear();
    setFormData({
      homeResort: contract.homeResort,
      useYearMonth: contract.useYearMonth,
      annualPoints: contract.annualPoints.toString(),
      currentYearPoints: contract.pointsRemaining[currentYear]?.toString() || '',
      nextYearPoints: contract.pointsRemaining[currentYear + 1]?.toString() || ''
    });
    setEditingContract(contract);
    setShowForm(true);
  };

  const handleDelete = (contractId) => {
    if (window.confirm('Are you sure you want to delete this contract?')) {
      setContracts(contracts.filter(contract => contract.id !== contractId));
    }
  };

  const getTotalPoints = () => {
    return contracts.reduce((total, contract) => total + contract.annualPoints, 0);
  };

  const getCurrentUseYearSummary = () => {
    return contracts.map(contract => {
      const currentUseYear = contract.getCurrentUseYear();
      const available = contract.getPointsRemaining(currentUseYear);
      return {
        contract,
        currentUseYear,
        available,
        used: contract.currentYearUsed,
        banked: contract.bankedPoints,
        borrowed: contract.borrowedPoints,
        totalPoints: contract.annualPoints
      };
    });
  };

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2>My DVC Contracts</h2>
        <button className="btn" onClick={() => setShowForm(true)}>
          <Plus size={16} style={{ marginRight: '8px' }} />
          Add Contract
        </button>
      </div>

      {contracts.length === 0 ? (
        <div className="alert alert-info">
          <p>No contracts added yet. Add your first DVC contract to get started with planning your vacations!</p>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: '24px' }}>
            {contracts.map(contract => {
              
              return (
                <div key={contract.id} className="card" style={{ margin: 0, border: '1px solid #ddd' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <div>
                      <h3 style={{ margin: 0, display: 'flex', alignItems: 'center' }}>
                        {(() => {
                          const resort = availableResorts[contract.homeResort] || DVC_RESORTS[contract.homeResort];
                          const IconComponent = getResortIcon(resort?.icon);
                          return <IconComponent size={16} style={{ marginRight: '8px', color: '#28a745' }} />;
                        })()}
                        {availableResorts[contract.homeResort]?.name || DVC_RESORTS[contract.homeResort]?.name || contract.homeResort}
                      </h3>
                      <p style={{ margin: '4px 0', color: '#666', fontSize: '14px' }}>
                        Use Year Start: {USE_YEAR_MONTHS.find(m => m.value === contract.useYearMonth)?.label}
                      </p>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button 
                        className="btn btn-secondary" 
                        style={{ padding: '6px 12px', fontSize: '12px' }}
                        onClick={() => handleEdit(contract)}
                      >
                        <Edit2 size={14} />
                      </button>
                      <button 
                        className="btn btn-danger" 
                        style={{ padding: '6px 12px', fontSize: '12px' }}
                        onClick={() => handleDelete(contract.id)}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                      <div style={{ fontSize: '16px', fontWeight: '600' }}>{contract.annualPoints} points/year</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>Annual Allocation</div>
                    </div>
                  </div>
                  
                  <div className="grid grid-4" style={{ gap: '12px' }}>
                    {Object.keys(contract.pointsRemaining).sort().slice(0, 4).map(year => {
                      const remaining = contract.getPointsRemaining(parseInt(year));
                      const maxSpendable = contract.getMaxSpendablePoints(parseInt(year));
                      const currentYear = new Date().getFullYear();
                      const isCurrentOrNext = parseInt(year) <= currentYear + 1;
                      
                      return (
                        <div key={year} style={{ 
                          padding: '16px', 
                          background: isCurrentOrNext ? '#f8fff9' : '#f8f9fa', 
                          borderRadius: '8px',
                          border: isCurrentOrNext ? '2px solid #28a745' : '2px solid #e9ecef',
                          textAlign: 'center'
                        }}>
                          <div style={{ fontSize: '18px', fontWeight: '700', marginBottom: '4px' }}>
                            {year}
                          </div>
                          
                          <div style={{ marginBottom: '12px' }}>
                            <div className="points-display" style={{ fontSize: '24px', marginBottom: '2px' }}>
                              {remaining}
                            </div>
                            <div style={{ fontSize: '11px', color: '#666' }}>
                              {'Remaining'}
                            </div>
                          </div>
                          <div style={{ marginBottom: '12px' }}>
                            <div className="points-display" style={{ fontSize: '24px', marginBottom: '2px' }}>
                              {maxSpendable}
                            </div>
                            <div style={{ fontSize: '11px', color: '#666' }}>
                            Max Spendable
                            </div>
                          </div>                      
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="alert alert-info">
            <strong>Portfolio Summary:</strong> {contracts.length} contract{contracts.length !== 1 ? 's' : ''} • {getTotalPoints()} total annual points
          </div>
        </>
      )}

      {showForm && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          background: 'rgba(0,0,0,0.5)', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="card" style={{ width: '90%', maxWidth: '500px', margin: 0 }}>
            <h3>{editingContract ? 'Edit Contract' : 'Add New Contract'}</h3>
            
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Home Resort *</label>
                <select 
                  className="form-control"
                  value={formData.homeResort}
                  onChange={(e) => setFormData({...formData, homeResort: e.target.value})}
                  required
                >
                  <option value="">Select a resort...</option>
                  {Object.entries(availableResorts)
                    .sort(([,a], [,b]) => a.name.localeCompare(b.name))
                    .map(([code, resort]) => (
                      <option key={code} value={code}>{resort.name}</option>
                    ))}
                </select>
              </div>

              <div className="form-group">
                <label>Use Year Start Month *</label>
                <select 
                  className="form-control"
                  value={formData.useYearMonth}
                  onChange={(e) => setFormData({...formData, useYearMonth: parseInt(e.target.value)})}
                  required
                >
                  {USE_YEAR_MONTHS.map(month => (
                    <option key={month.value} value={month.value}>{month.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Annual Points *</label>
                <input 
                  type="number"
                  className="form-control"
                  value={formData.annualPoints}
                  onChange={(e) => setFormData({...formData, annualPoints: e.target.value})}
                  placeholder="e.g., 150"
                  min="1"
                  required
                />
              </div>


              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Points Remaining</label>
                <div className="grid grid-2">
                  <div className="form-group">
                    <label>Current Use Year ({new Date().getFullYear()})</label>
                    <input 
                      type="number"
                      className="form-control"
                      value={formData.currentYearPoints}
                      onChange={(e) => setFormData({...formData, currentYearPoints: e.target.value})}
                      placeholder={formData.annualPoints || "0"}
                      min="0"
                    />
                  </div>
                  <div className="form-group">
                    <label>Next Use Year ({new Date().getFullYear() + 1})</label>
                    <input 
                      type="number"
                      className="form-control"
                      value={formData.nextYearPoints}
                      onChange={(e) => setFormData({...formData, nextYearPoints: e.target.value})}
                      placeholder={formData.annualPoints || "0"}
                      min="0"
                    />
                  </div>
                </div>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  Future years will be calculated at your annual allocation. Leave blank to use annual allocation.
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
                <button type="submit" className="btn">
                  {editingContract ? 'Update Contract' : 'Add Contract'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractManager;
